import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { NodeView } from '@tiptap/pm/view';
import lodash from 'lodash';

import { CELL_AUTO_MIN_WIDTH } from '../../consts';
import { ROOT } from '../../data-hooks';
import { updateColumns } from './resizeUtils';
import styles from './table-scss';
export class TableView implements NodeView {
  node: ProseMirrorNode;

  table: HTMLElement;

  colgroup: Element;

  contentDOM: HTMLElement;

  editor: Editor;

  dom: HTMLElement;

  wrapper: HTMLElement;

  resizeObserver?: ResizeObserver;

  constructor(node: ProseMirrorNode, editor: Editor) {
    this.editor = editor;
    this.node = node;
    this.dom = document.createElement('div');
    this.wrapper = document.createElement('div');
    this.dom.appendChild(this.wrapper);

    this.wrapper.className = styles.tableWrapper;

    this.table = this.wrapper.appendChild(document.createElement('table'));
    this.table.dataset.hook = ROOT;
    this.colgroup = this.table.appendChild(document.createElement('colgroup'));

    updateColumns({
      node,
      colgroup: this.colgroup,
      table: this.table,
      cellMinWidth: CELL_AUTO_MIN_WIDTH,
      commands: this.editor.commands,
      shouldUpdateState: true,
    });

    this.contentDOM = this.table.appendChild(document.createElement('tbody'));

    if (!window?.ResizeObserver || !this.dom.parentElement) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() =>
      handleResize(this.dom, this.node, this.colgroup)
    );

    this.resizeObserver.observe(this.editor.view.dom);
  }

  update(node: ProseMirrorNode) {
    if (node.type !== this.node.type) {
      return false;
    }

    this.node = node;

    updateColumns({
      node,
      colgroup: this.colgroup,
      table: this.table,
      cellMinWidth: CELL_AUTO_MIN_WIDTH,
      commands: this.editor.commands,
      shouldUpdateState: true,
    });

    return true;
  }

  ignoreMutation(mutation: MutationRecord | { type: 'selection'; target: Element }) {
    return (
      mutation.type === 'attributes' &&
      (mutation.target === this.table || this.colgroup.contains(mutation.target))
    );
  }

  destroy() {
    this.resizeObserver?.disconnect();
  }
}

const handleResize = lodash.debounce((tableWrapper, node, colgroup) => {
  const tableWrapperWidth = tableWrapper.offsetWidth - 48;
  const tableWidth = Array.from(colgroup.children).reduce(
    (totalWidth: number, curr) => totalWidth + (curr as HTMLElement).offsetWidth,
    0
  );

  const { colsWidthRatio, colsMinWidth } = node.attrs.dimensions;
  const shouldResize =
    colsWidthRatio &&
    !colsWidthRatio.find(
      (width: $TSFixMe) =>
        ((tableWidth as number) * width) / tableWrapperWidth <= CELL_AUTO_MIN_WIDTH
    );
  shouldResize &&
    Array.from(colgroup.children).forEach((col: $TSFixMe, index: number) => {
      const colMinWidth = Math.min(colsMinWidth[index], CELL_AUTO_MIN_WIDTH);
      const colWidth: number = Math.floor(
        ((tableWidth as number) * colsWidthRatio[index]) / tableWrapperWidth
      );
      col.style.width = Math.max(colWidth, colMinWidth) + 'px';
    });
}, 60);
